import PropTypes from "prop-types";
import { useState } from "react";
import { Helmet } from "react-helmet-async";
import {
  Alert,
  Button,
  Card,
  CardContent,
  Container,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import AppUserSearchTable from "src/components/users/AppUserSearchTable";
import { post } from "src/utils/networkUtils";

const CHANNELS = [
  { value: "in_app", label: "In-app" },
  { value: "push", label: "Push" },
  { value: "email", label: "Email" },
  { value: "sms", label: "SMS" },
];

function ActionFields({ action, form, onChange }) {
  if (action === "communication_request") {
    return (
      <>
        <TextField
          select
          label="Channel"
          value={form.channel}
          onChange={(event) => onChange("channel", event.target.value)}
          fullWidth
        >
          {CHANNELS.map((channel) => (
            <MenuItem key={channel.value} value={channel.value}>
              {channel.label}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          label="Subject"
          value={form.subject}
          onChange={(event) => onChange("subject", event.target.value)}
          fullWidth
        />
        <TextField
          label="Message"
          value={form.message}
          onChange={(event) => onChange("message", event.target.value)}
          minRows={5}
          multiline
          required
          fullWidth
        />
      </>
    );
  }

  return (
    <TextField
      label={
        action === "gdpr_deletion_request"
          ? "Request reference / notes"
          : "Reason"
      }
      value={form.note}
      onChange={(event) => onChange("note", event.target.value)}
      minRows={4}
      multiline
      fullWidth
    />
  );
}

ActionFields.propTypes = {
  action: PropTypes.string.isRequired,
  form: PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired,
};

export default function UserAdminActionPage({
  action,
  actionLabel,
  description,
  successLabel,
  title,
}) {
  const [selectedUser, setSelectedUser] = useState(null);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState("");
  const [error, setError] = useState("");
  const [form, setForm] = useState({
    note: "",
    channel: "in_app",
    subject: "",
    message: "",
  });
  const [confirmValue, setConfirmValue] = useState("");
  const [erasing, setErasing] = useState(false);
  const [erasure, setErasure] = useState(null);
  const [erasureError, setErasureError] = useState("");

  const updateForm = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setError("");
    setSuccess("");
  };

  const selectUser = (user) => {
    setSelectedUser(user);
    setError("");
    setSuccess("");
    setConfirmValue("");
    setErasure(null);
    setErasureError("");
  };

  const executeErasure = async () => {
    if (!selectedUser || !confirmValue.trim()) return;
    setErasing(true);
    setErasureError("");
    setErasure(null);
    try {
      const response = await post(
        `/admin/app-users/${selectedUser.id}/anonymise`,
        { confirm: confirmValue.trim(), note: form.note || null }
      );
      setErasure(response?.data?.data || null);
      setConfirmValue("");
    } catch (err) {
      setErasureError(
        err?.response?.data?.message || "Failed to anonymise this user."
      );
    } finally {
      setErasing(false);
    }
  };

  const submit = async () => {
    if (!selectedUser) return;
    if (action === "communication_request" && !form.message.trim()) {
      setError("Message is required.");
      return;
    }

    setSaving(true);
    setError("");
    setSuccess("");
    try {
      await post(`/admin/app-users/${selectedUser.id}/actions`, {
        action,
        note: form.note || null,
        channel: form.channel,
        subject: form.subject || null,
        message: form.message || null,
      });
      setSuccess(successLabel);
      setForm({ note: "", channel: "in_app", subject: "", message: "" });
    } catch (_err) {
      setError("Failed to save this action.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>{title} | DreamTeam Admin</title>
      </Helmet>

      <Container maxWidth="xl">
        <Stack spacing={3}>
          <Stack spacing={1}>
            <Typography variant="h4">{title}</Typography>
            <Typography variant="body2" color="text.secondary">
              {description}
            </Typography>
          </Stack>

          <AppUserSearchTable
            actionLabel="Select"
            onSelectUser={selectUser}
            searchLabel="Find user"
          />

          <Card>
            <CardContent>
              <Stack spacing={2}>
                <Typography variant="h6">Action</Typography>
                {selectedUser ? (
                  <Alert severity="info">
                    Selected user: {selectedUser.username || selectedUser.refId}
                  </Alert>
                ) : (
                  <Alert severity="warning">
                    Select a user above before saving an action.
                  </Alert>
                )}
                {success && <Alert severity="success">{success}</Alert>}
                {error && <Alert severity="error">{error}</Alert>}
                <ActionFields
                  action={action}
                  form={form}
                  onChange={updateForm}
                />
                <Button
                  variant="contained"
                  disabled={!selectedUser || saving}
                  onClick={submit}
                  sx={{ alignSelf: "flex-start" }}
                >
                  {saving ? "Saving..." : actionLabel}
                </Button>
              </Stack>
            </CardContent>
          </Card>

          {action === "gdpr_deletion_request" && (
            <Card>
              <CardContent>
                <Stack spacing={2}>
                  <Typography variant="h6">Execute erasure (irreversible)</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Anonymises the selected user in place (username, CPN, Gaming
                    Hub id, club and country are replaced), deletes their fantasy
                    team and history, mini league memberships, activity,
                    notification and gamification records, and clears the
                    related caches. Gaming Hub must still be told so they can
                    close the account on their side. Type the user&apos;s CPN or
                    username to confirm.
                  </Typography>
                  {erasure && (
                    <Alert severity="success">
                      User anonymised at {erasure.anonymisedAt}. Teams deleted:{" "}
                      {erasure.teamsDeleted}
                      {erasure.teamsRetained
                        ? ` (retained and renamed because of prize records: ${erasure.teamsRetained})`
                        : ""}
                      . League entries removed: {erasure.leagueEntriesDeleted}.
                      Activity rows: {erasure.activityLogDeleted}. Notifications:{" "}
                      {erasure.notificationsDeleted}. Gamification rows:{" "}
                      {erasure.gamificationRowsDeleted}. Gaming Hub notified:{" "}
                      {erasure.gamingHub?.notified ? "yes" : "no"} (
                      {erasure.gamingHub?.detail}).
                    </Alert>
                  )}
                  {erasureError && <Alert severity="error">{erasureError}</Alert>}
                  <TextField
                    label="Type the user's CPN or username to confirm"
                    value={confirmValue}
                    onChange={(event) => setConfirmValue(event.target.value)}
                    disabled={!selectedUser || erasing}
                    fullWidth
                  />
                  <Button
                    color="error"
                    variant="contained"
                    disabled={!selectedUser || !confirmValue.trim() || erasing}
                    onClick={executeErasure}
                    sx={{ alignSelf: "flex-start" }}
                  >
                    {erasing ? "Anonymising..." : "Anonymise user now"}
                  </Button>
                </Stack>
              </CardContent>
            </Card>
          )}
        </Stack>
      </Container>
    </>
  );
}

UserAdminActionPage.propTypes = {
  action: PropTypes.oneOf([
    "suspension_request",
    "gdpr_deletion_request",
    "communication_request",
  ]).isRequired,
  actionLabel: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  successLabel: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
};
